import type { ContentNode, DocumentState } from '../document-types';
import type { NodeHandler, RenderContext } from './types';
import type { DocumentHandlerContext } from '../document-handler.type';
import { LLMMessage } from '@cotera/api';
import { extractSymbolReferencesFromEraql } from '../extract-symbol-references';
import { EraQL } from '@cotera/era';
import mime from 'mime-types';

/**
 * Handler for attachment nodes.
 * Removes attachment content from the main prompt rendering.
 */
export class AttachmentHandler implements NodeHandler {
  readonly type = 'attachment';

  render(
    _node: ContentNode | DocumentState,
    _parent: ContentNode | DocumentState | null,
    _childContent: string,
    _ctx: RenderContext
  ): string {
    // Return empty string to remove attachment from main prompt
    return '';
  }

  getDependencies(node: ContentNode, _ctx: DocumentHandlerContext): string[] {
    const value = node.attrs?.['value'] as string | undefined;
    const isFreehand = (node.attrs?.['isFreehand'] as boolean) ?? false;

    // Only extract from value if it's not freehand (i.e., it's an eraql expression)
    if (value && !isFreehand) {
      return extractSymbolReferencesFromEraql(value);
    }
    return [];
  }

  replaceReference(
    node: ContentNode,
    oldReference: string,
    newReference: string
  ): ContentNode | null {
    const value = node.attrs?.['value'] as string | undefined;
    const isFreehand = (node.attrs?.['isFreehand'] as boolean) ?? false;

    // Only replace in value if it's not freehand (i.e., it's an eraql expression)
    if (value && !isFreehand) {
      const updatedValue = EraQL.renameSymbolInEraql({
        from: oldReference,
        to: newReference,
        eraql: value,
      });
      if (updatedValue !== value) {
        return {
          ...node,
          attrs: {
            ...node.attrs,
            value: updatedValue,
          },
        };
      }
    }
    return node;
  }

  getExtraMessages(
    node: ContentNode,
    ctx: DocumentHandlerContext
  ): LLMMessage[] {
    const rawValue = node.attrs?.['value'];
    const isFreehand = (node.attrs?.['isFreehand'] as boolean) || false;

    // Ensure value is a valid non-empty string
    // Convert to string if it's not already, but only if it's a valid value
    if (rawValue == null) {
      return [];
    }

    const value = typeof rawValue === 'string' ? rawValue : String(rawValue);
    if (value.trim().length === 0) {
      return [];
    }

    let fileData: string;

    if (!isFreehand) {
      // Not freehand - treat as expression
      if (ctx.mode === 'expression') {
        // In expression mode, leave as-is
        fileData = value;
      } else {
        // In simple mode, evaluate the expression
        // Wrap parseExpr in try-catch to handle any errors
        try {
          const parseResult = ctx.graph.parser.parseExpr(value);

          if (parseResult.isErr()) {
            // If parsing fails, use the value as-is
            fileData = value;
          } else {
            // Evaluate the expression
            const evaluated = parseResult.value.evaluate();
            fileData = evaluated?.toString() ?? '';
          }
        } catch (error) {
          // If parsing throws an error, use the value as-is
          fileData = value;
        }
      }
    } else {
      // Freehand - treat as file reference/URL
      fileData = value;
    }

    if (!fileData) {
      return [];
    }

    return [
      {
        role: 'file',
        data:
          ctx.mode === 'expression'
            ? `format_nullable\`{{ "${fileData}" }}\``
            : fileData,
      },
    ];
  }

  /**
   * Infer media type from file value/URL
   * Extracts filename/extension from URLs (including query parameters) and uses mime-types library
   */
  private inferMediaType(value: string): string {
    let filename: string | undefined;

    // Try to parse as URL first to handle query parameters
    try {
      const url = new URL(value);
      // Check pathname for filename
      if (url.pathname) {
        const pathnameParts = url.pathname.split('/');
        const lastPart = pathnameParts[pathnameParts.length - 1];
        if (lastPart && lastPart.includes('.')) {
          filename = lastPart;
        }
      }
      // If no filename in pathname, check query parameters (e.g., ?name=file.png)
      if (!filename) {
        const nameParam = url.searchParams.get('name');
        if (nameParam) {
          filename = nameParam;
        }
      }
    } catch {
      // Not a valid URL, treat as filename/path
      filename = value;
    }

    // Use mime-types library to lookup MIME type
    if (filename) {
      const mimeType = mime.lookup(filename);
      if (mimeType !== false) {
        return mimeType;
      }
    }

    //HACK - Default to image/png if we can't infer
    //this is to support our only existing use cases
    //should fix
    return 'image/png';
  }
}
